import { useRef, useState, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { ApiError } from '../api/client';
import { createIdempotencyKeyHolder } from '../api/idempotency';
import { useSession } from '../auth/useSession';
import { ErrorBanner } from '../components/ErrorBanner';
import { FormField } from '../components/FormField';

const MIN_PASSWORD_LENGTH = 8;

/**
 * Forced first-access step (docs/FLOWS.md §2): reached only while
 * session.mustChangePassword is true (guarded by RequireMustChangePassword
 * in router.tsx), rendered chromeless like Entrar (see App.tsx Shell), no way
 * out except finishing this form or Salir. The endpoint contract requires
 * `currentPassword` on every change — the flow breadboard only sketches the
 * new-password fields, so this form also asks for the (temporary) current
 * one to satisfy that contract; nothing else about the flow deviates.
 */
export function CambiarContrasenaPage() {
  const { changePassword, logout } = useSession();
  const navigate = useNavigate();
  const idempotencyKey = useRef(createIdempotencyKeyHolder()).current;

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(undefined);

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`La contraseña nueva debe tener al menos ${MIN_PASSWORD_LENGTH} caracteres.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Las contraseñas nuevas no coinciden.');
      return;
    }

    setSubmitting(true);
    try {
      await changePassword(currentPassword, newPassword, idempotencyKey.ensure());
      await navigate({ to: '/' });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
      idempotencyKey.reset();
    }
  }

  return (
    <main className="auth">
      <div className="auth-card">
        <p className="brand" aria-hidden="true">
          <span className="brand-word">
            Dale<span className="dot">.</span>
          </span>
          <span className="brand-sub">Negocios</span>
        </p>
        <h1>Cambia tu contraseña</h1>
        <p className="lede">Tu contraseña es temporal. Elige la tuya.</p>
        {error ? <ErrorBanner message={error} /> : null}
        <form onSubmit={handleSubmit}>
          <FormField label="Contraseña actual" htmlFor="current-password">
            <input
              id="current-password"
              name="currentPassword"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              required
            />
          </FormField>
          <FormField label="Contraseña nueva" htmlFor="new-password">
            <input
              id="new-password"
              name="newPassword"
              type="password"
              autoComplete="new-password"
              minLength={MIN_PASSWORD_LENGTH}
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              required
            />
          </FormField>
          <FormField label="Repite la contraseña nueva" htmlFor="confirm-password">
            <input
              id="confirm-password"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              minLength={MIN_PASSWORD_LENGTH}
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              required
            />
          </FormField>
          <button type="submit" className="btn" disabled={submitting}>
            {submitting ? 'Guardando…' : 'Cambiar contraseña'}
          </button>
          <button type="button" className="linklike" onClick={() => void logout()}>
            Salir
          </button>
        </form>
      </div>
    </main>
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos completar eso ahora mismo.';
}
